import { composeSetName, equationEvaluability, equationProblem, fieldsForKind, fieldsForRounding, formulaDependencies, isDegenerateSkew, MAX_EQUATION_DECIMALS, MAX_EQUATION_SIG_FIGS, MAX_SKEW, MIN_EQUATION_DECIMALS, MIN_EQUATION_SIG_FIGS, MIN_SKEW, resolveVariables, setLeaf, setsIn, skewModeValue, variableKind, wouldCycle, type VariableKind } from '@agavi/blocks';
import { ArrowLeftRight, Braces, ChevronDown, ChevronRight, CopyPlus, CornerDownRight, Dices, Hash, Sigma, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormulaField } from './formula-field';
import { EditorSectionHeading } from './input-block-frame';
import { drawRandomValue, fmtResolved, isRollableVariable } from './variable-chip-preview';
import { isSavableVariable } from './variable-utils';

import type { TaskVariable } from '@/types/experience';

import { AddRowButton } from '@/components/ui/add-row-button';
import { ARMED_DELETE_PAINT } from '@/components/ui/armed-delete';
import { IconButton } from '@/components/ui/icon-button';
import { NumberField } from '@/components/ui/number-field';
import { RangeField } from '@/components/ui/range-field';
import { TextField } from '@/components/ui/text-field';
import { freeSetName } from '@/lib/variable-sets';

interface VariablesPanelProps {
  variables: TaskVariable[];
  /**
   * The experience's variables, which every task in it can see.
   *
   * A second list rather than a flag on each row: the two are edited separately and a row never
   * moves between them by being toggled, so nothing is served by storing them together and
   * partitioning on render.
   */
  experienceVariables: TaskVariable[];
  /**
   * Names a promotion was offered for and declined.
   *
   * They belong to task variables in other tasks, so no row here holds them and the ordinary
   * duplicate check cannot see them. Feeding them in makes a declined name read exactly like a
   * duplicate, which is what it is: a name already spoken for.
   */
  declinedNames?: string[];
  /** Clears the refusals above — see the row's `onNameEdited`. */
  onRetryName?: () => void;
  /** Maps variable id → true if that variable is referenced in at least one block. For an
   *  EXPERIENCE variable that has to mean anywhere in the experience, not just the open task. */
  variableUsage?: Record<string, VariableUsage>;
  onAddVariable: (v: TaskVariable) => void;
  /**
   * MAY A NEW ONE BE MADE — which is a different question from whether the panel opens.
   *
   * The panel itself survives the capability being switched off, because it is the only screen
   * that can delete a variable and a task left randomizing with no way to stop it is a trapdoor.
   * What goes is the `+`: gate adoption, never abandonment. Every row keeps its trash.
   */
  mayAdd?: boolean;
  onUpdateVariable: (id: string, patch: Partial<Omit<TaskVariable, 'id'>>) => void;
  onRemoveVariable: (id: string) => void;
  /** Every set operation is ONE operation on the page, never one call per member: the undo stack
   *  takes one step for what the teacher did with one press. */
  onRenameSet: (from: string, to: string) => void;
  onDeleteSet: (set: string) => void;
  onDuplicateSet: (set: string) => void;
  onAddExperienceVariable: (v: TaskVariable) => void;
  onUpdateExperienceVariable: (id: string, patch: Partial<Omit<TaskVariable, 'id'>>) => void;
  onRemoveExperienceVariable: (id: string) => void;
  onRenameExperienceSet: (from: string, to: string) => void;
  onDeleteExperienceSet: (set: string) => void;
  onDuplicateExperienceSet: (set: string) => void;
  onClose: () => void;
  /** Strip outer border/radius and expand to fill container (phone full-screen mode). */
  fullScreen?: boolean;
}

// ─── Utilities ────────────────────────────────────────────────────────────────

/**
 * Why a variable cannot be deleted, rather than merely that it cannot.
 *
 * The two reasons send a teacher to different places: `blocks` means hunt for chips in the task's
 * questions, `formula` means another variable is computed from this one and nothing in any block
 * mentions it. One boolean could only ever describe the first, which is the hunt that never ends.
 */
export type VariableUsage =
  | false
  /**
   * Where it is used, named by the page — the only layer that knows task titles.
   *
   * `kind` is what the leftover count is counting. THIS TASK FIRST: if the variable appears in a
   * block here, the named places are those blocks and the rest are "other places". If it appears
   * only elsewhere, naming a block is useless — "MCQ2" in a task you are not looking at sends you
   * to the wrong screen — so the names become TASKS and the rest are "other tasks".
   */
  | { kind: 'places' | 'tasks'; names: string[]; more: number }
  /** The variable whose equation stands on this one. */
  | { kind: 'formula'; dependent: string };

/**
 * A set's own header: what it is called, and the two things you do to a set as a whole.
 *
 * Duplicate sits beside the name because it is the reason to have sets at all — the owner's
 * case was five stars, and copying one is thirty rows you do not type. Delete is a set-level
 * operation for the same reason: members leave together, so the per-variable guard does not
 * apply and a set does not have to be dismantled in reverse dependency order.
 */
/**
 * A set's header: its name, and the caret that opens it.
 *
 * CARET ON THE RIGHT, and nothing else up here, which is the convention the feedback and event
 * cards already set. Duplicate and Delete moved to the open card's footer with them: they are
 * the two destructive-ish things in the panel, and a header is read far more often than it is
 * acted on. Keeping them visible on every collapsed set put two buttons beside every name for
 * the sake of an action taken once.
 *
 * COLLAPSED, the header names its members. A set closed to one line is otherwise a name and no
 * answer to the only question worth asking of it — what is in there — and the teacher has to
 * open it to find out, which defeats collapsing it.
 */
function SetHeader({ set, members, open, roomyTargets, allNames, rolls, onToggle, onRename }: {
  set: string;
  members: TaskVariable[];
  open: boolean;
  roomyTargets: boolean;
  allNames: string[];
  /** Resolved values by variable id, so a closed set can show what it is currently worth. */
  rolls: Record<string, string>;
  onToggle: () => void;
  onRename: (next: string) => void;
}) {
  const { t } = useTranslation();
  // A draft, same as a variable's name, and for the same reason: a rename rewrites every
  // member's composed name and every chip pointing at them, so it must happen once, on purpose.
  const [draft, setDraft] = useState(set);
  useEffect(() => { setDraft(set); }, [set]);

  const clash = !!draft && draft !== set
    && (allNames.includes(draft) || allNames.some(n => n.startsWith(draft + '_')));
  const invalid = !!draft && (!/^[a-zA-Z][a-zA-Z0-9]*$/.test(draft) || clash);

  return (
    <div className='flex items-start gap-1.5 rounded-t-lg bg-ws-text-link/[.06] px-2 py-1 dark:bg-ws-text-link/[.10]'>
      <TextField
        variant='prose'
        value={draft}
        onChange={v => setDraft(v.replace(/[^a-zA-Z0-9]/g, ''))}
        onBlur={() => { if (!invalid && draft) onRename(draft); else setDraft(set); }}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
          if (e.key === 'Escape') { e.preventDefault(); setDraft(set); }
        }}
        hasError={invalid}
        maxLength={10}
        ariaLabel={t('task.settings.variables.setNameLabel')}
        className='w-[104px] shrink-0' />

      {/* What is in there, for a set you are not looking inside. Leaves only: the set's own name
          is in the field immediately to the left, and repeating it here would spend the whole
          line on it. */}
      {!open && (
        /*
         * NAME AND CURRENT VALUE, per member: `mass 2.6, lum 28.34`.
         *
         * The names alone restated what the card already was. Once there are star1, star2 and
         * star3, three cards each listing the same five leaves is the same sentence three times
         * — what is actually worth comparing is the numbers. With them here you can reroll from
         * the panel header and read every set's draw without opening anything.
         *
         * WRAPS, and the card grows to fit. Truncating defeats the point of closing a set: what
         * it holds is the one thing a closed set is being asked, and a nine-member set ending in
         * an ellipsis has to be opened to answer it.
         */
        <span className='min-w-0 flex-1 self-center break-words py-0.5 text-xs leading-snug text-ws-muted'>
          {members.length === 0
            ? t('task.settings.variables.setEmpty')
            : members.map((m, i) => (
              <span key={m.id}>
                {i > 0 && ', '}
                {setLeaf(m)}
                {rolls[m.id] != null && (
                  <> <span className='font-medium text-ws-text-link'>{rolls[m.id]}</span></>
                )}
              </span>
            ))}
        </span>
      )}
      {open && <span className='flex-1' />}

      <IconButton
        size={roomyTargets ? 'lg' : 'sm'}
        onClick={onToggle}
        aria-expanded={open}
        aria-label={t(open ? 'task.settings.variables.collapseSet' : 'task.settings.variables.expandSet', { name: set })}
        className='self-start'>
        {open ? <ChevronDown aria-hidden='true' className='h-3.5 w-3.5' />
          : <ChevronRight aria-hidden='true' className='h-3.5 w-3.5' />}
      </IconButton>
    </div>
  );
}

/**
 * The open set's footer: what you can add to it, and what you can do to it.
 *
 * Duplicate and Delete live here rather than in the header because an open card is a card you
 * are working on. Delete ARMS on the first press and goes on the second, which is the rule
 * every other destructive button in this app follows — and it matters more here than on a row,
 * because one press takes the whole set and every member in it.
 */
function SetFooter({ set, blocked, armed, roomyTargets, onAdd, onArm, onDisarm, onDuplicate, onDelete }: {
  set: string;
  blocked: { reason: 'blocks' | 'formula'; name: string } | false;
  armed: boolean;
  roomyTargets: boolean;
  /** Absent once the capability is off: the `+` is adoption, the trash beside it is the exit. */
  onAdd?: (kind: VariableKind) => void;
  onArm: () => void;
  onDisarm: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const blockedReason = blocked === false ? undefined
    : blocked.reason === 'blocks'
      // BRACED, both branches. A variable name is never bare text in this app, and the formula
      // branch below already knew that — so the two halves of one message disagreed about how to
      // write the same name.
      ? t('task.settings.variables.deleteSetBlockedBlocks', { name: `{${blocked.name}}` })
      : t('task.settings.variables.deleteSetBlockedFormula', { dependent: `{${blocked.name}}` });

  return (
    <div className='flex items-center gap-1.5 pt-1'>
      {onAdd
        ? <AddKindButtons onAdd={onAdd} roomyTargets={roomyTargets} className='min-w-0 flex-1' />
        /* The spacer keeps duplicate and delete at the right-hand end, where they sit in every
           other set footer on the panel. Without it they slide left and the two footers of one
           panel disagree about where its destructive control lives. */
        : <div className='min-w-0 flex-1' />}

      <IconButton size='sm' onClick={onDuplicate}
        aria-label={t('task.settings.variables.duplicateSet', { name: set })}
        title={t('task.settings.variables.duplicateSet', { name: set })}>
        <CopyPlus aria-hidden='true' className='h-3.5 w-3.5' />
      </IconButton>

      <IconButton
        variant='destructive'
        size='sm'
        isArmed={armed}
        disabled={!!blocked}
        onClick={() => { if (armed) onDelete(); else onArm(); }}
        onPointerLeave={e => { if (e.pointerType !== 'touch' && armed) onDisarm(); }}
        onBlur={onDisarm}
        onKeyDown={e => { if (e.key === 'Escape' && armed) { e.stopPropagation(); onDisarm(); } }}
        title={blockedReason}
        aria-label={blockedReason ?? t(armed
          ? 'task.settings.variables.deleteSetConfirm'
          : 'task.settings.variables.deleteSet', { name: set })}>
        <Trash2 aria-hidden='true' className='h-3.5 w-3.5' />
      </IconButton>
    </div>
  );
}

/**
 * The row of controls for making something new: one per variable type, plus a set.
 *
 * Extracted because it renders in two places that must not drift — a section's footer and every
 * set's own footer. A set needs its own, or "add a variable INTO starA" has nowhere to live.
 */
function AddKindButtons({ onAdd, onAddSet, roomyTargets, addBtnRef, className = '' }: {
  onAdd: (kind: VariableKind) => void;
  /** Absent inside a set: sets do not nest. */
  onAddSet?: () => void;
  roomyTargets: boolean;
  addBtnRef?: React.Ref<HTMLButtonElement>;
  className?: string;
}) {
  const { t } = useTranslation();
  // THE SHARED CONTROL, which already names the variables panel among the lists it serves. This
  // was a hand-rolled copy of it; `iconOnly` and `grow` were added there rather than kept here,
  // so the dashed treatment stays in one place.
  //
  // FULL WIDTH between them: three small squares at the left edge of a 350px panel read as
  // something left over, where a stretched row is a deliberate footer under the list it adds to.
  return (
    <div className={`flex items-stretch gap-1.5 ${className}`}>
      {KIND_ORDER.map((kind, i) => (
        <AddRowButton
          key={kind}
          ref={i === 1 ? addBtnRef : undefined}
          label={t(ADD_LABEL_KEY[kind])}
          onClick={() => onAdd(kind)}
          icon={<KindGlyph kind={kind} />}
          iconOnly
          grow
          touch={roomyTargets}
        />
      ))}
      {onAddSet && (
        // The only one that keeps its word. A set is not a fourth kind of variable, so it does
        // not have a glyph the rows have already taught; `items-stretch` keeps it the same
        // height as the three beside it.
        <AddRowButton
          label={t('task.settings.variables.addSet')}
          ariaLabel={t('task.settings.variables.addSetLabel')}
          onClick={onAddSet}
          icon={<CornerDownRight aria-hidden='true' />}
          inline
          grow
        />
      )}
    </div>
  );
}

/** Constants first — they are the givens, and a teacher reads them as the setup rather than as
 *  part of the randomisation. Equations last, since they stand on the other two. */
const KIND_ORDER: VariableKind[] = ['constant', 'random', 'equation'];

/** The server refuses anything outside this, so the field must not be able to send it. */
const clampDecimals = (n: number) =>
  Math.min(MAX_EQUATION_DECIMALS, Math.max(MIN_EQUATION_DECIMALS, Math.round(n)));

const clampSigFigs = (n: number) =>
  Math.min(MAX_EQUATION_SIG_FIGS, Math.max(MIN_EQUATION_SIG_FIGS, Math.round(n)));

/** The ceiling is not cosmetic: past it a variable mostly rolls one value whatever its range.
 *  The floor is 51 because 50 is the uniform draw, which is spelled as no skew at all. */
const clampSkew = (n: number) =>
  Math.min(MAX_SKEW, Math.max(MIN_SKEW, Math.round(n)));

/** What pressing Skew writes. Strong enough to be obviously doing something — it takes the
 *  owner's 0.08-to-25 stellar range from 7.7% under two solar masses to 44% — and far enough
 *  below the cap that it is a starting point rather than a limit. */
const DEFAULT_SKEW = 80;

/**
 * The shape of the draw, as sixteen bars.
 *
 * Here because a strength number alone is not readable: 80 and 90 are both "quite skewed" until
 * you see that one leans and the other collapses. Index space, which is also value space — the
 * grid is linear, so the k-th sixteenth of the indices is the k-th sixteenth of the range.
 *
 * `aria-hidden`, and deliberately: it restates the strength field beside it and the warning
 * below it, so a screen reader that skips it loses nothing. It is not the only thing saying this.
 */
function SkewPreview({ variable, className }: { variable: TaskVariable; className?: string }) {
  const bars = 16;
  const e = Math.log(Math.min(variable.skew ?? 50, MAX_SKEW) / 100) / Math.log(0.5);
  const heights: number[] = [];
  let prev = 0;
  for (let k = 0; k < bars; k++) {
    const cum = Math.pow((k + 1) / bars, e);
    heights.push(cum - prev);
    prev = cum;
  }
  if (variable.skewToward === 'max') heights.reverse();
  const peak = Math.max(...heights, Number.EPSILON);
  /*
   * COMPRESSED, so the silhouette stays a ramp.
   *
   * At 95% the true heights are one full bar and fifteen at about 1px, which draws a single
   * stroke — at icon size that reads as the digit 1, not as a distribution. Raising the
   * normalised heights to a fractional power keeps the ORDER exactly right and only flattens the
   * ratio, so the shape still leans the way the draw does and is still a shape.
   *
   * The slider and its readout carry the actual strength; this has to say "skewed, that way".
   */
  const shaped = heights.map(h => Math.pow(h / peak, 0.3));
  return (
    <svg
      aria-hidden='true'
      viewBox={`0 0 ${bars * 3} 14`}
      preserveAspectRatio='none'
      className={`shrink-0 ${className ?? 'h-4 w-12 text-ws-text-link'}`}>
      {shaped.map((h, k) => {
        const tall = Math.max(1, h * 13);
        return <rect key={k} x={k * 3} y={14 - tall} width={2} height={tall} fill='currentColor' />;
      })}
    </svg>
  );
}

/** Spelled out rather than built as `add_${kind}`: a template-literal key cannot be grepped, and
 *  `invariants.test.ts` caps how many of those exist for exactly that reason. */
const TYPE_NAME_KEY: Record<VariableKind, string> = {
  constant: 'task.settings.variables.typeConstant',
  random:   'task.settings.variables.typeRandom',
  equation: 'task.settings.variables.typeEquation',
};

const ADD_LABEL_KEY: Record<VariableKind, string> = {
  constant: 'task.settings.variables.add_constant',
  random:   'task.settings.variables.add_random',
  equation: 'task.settings.variables.add_equation',
};

/**
 * An equation as a collapsed row says it: the expression with the braces taken off, and inside a
 * set, without the set's own prefix.
 *
 * Braces go because a `{name}` is a chip or a resolved number everywhere in this app and never
 * literal text, and a chip does not fit in 86px. The prefix goes for the same reason the member's
 * name does: the set names itself in the header directly above, so `starA_mass^3.5` spends a
 * third of the space repeating it and then truncates. A reference to ANOTHER set keeps its
 * prefix, because there the name is the only thing saying which star is meant.
 */
function summariseFormula(formula: string, set?: string): string {
  const bare = formula.replace(/[{}]/g, '').trim();
  return set ? bare.replace(new RegExp(`\\b${set}_`, 'g'), '') : bare;
}

/** One glyph per type, so the row says what it is before any field is read. `Hash` for a fixed
 *  number, `Dices` for one that is rolled, `Sigma` for one that is computed. */
function KindGlyph({ kind }: { kind: VariableKind }) {
  const Icon = kind === 'constant' ? Hash : kind === 'equation' ? Sigma : Dices;
  return <Icon aria-hidden='true' className='h-3.5 w-3.5' />;
}

function fmtField(n: number): string {
  const abs = Math.abs(n);
  if (n !== 0 && (abs < 1e-5 || abs >= 1e5)) {
    return n.toExponential().replace('e+', 'e');
  }
  return String(n);
}

/**
 * What the sample strip actually remembers: one drawn number per RANDOM, keyed by id.
 *
 * Constants and equations are deliberately absent. A constant needs no draw, and an equation must
 * never be remembered — it is recomputed from whatever its inputs currently are, so that adding
 * the variable an equation was waiting on makes the equation resolve, and editing a seed moves
 * everything downstream of it. A derived value held over from an earlier state would be a number
 * that is not the value of its own equation.
 *
 * `keep` is what makes the strip sticky across an unrelated edit: an existing random holds its
 * draw, and only the Reroll button (which passes nothing) draws again.
 */
function drawRandoms(vars: TaskVariable[], keep: Record<string, number> = {}): Record<string, number> {
  const draws: Record<string, number> = {};
  for (const v of vars) {
    if (!isRollableVariable(v)) continue;
    const held = keep[v.id];
    draws[v.id] = held != null && canStillDraw(v, held) ? held : drawRandomValue(v);
  }
  return draws;
}

/**
 * Could this variable still produce the number it is currently showing?
 *
 * Sticky draws are the point — a teacher editing one variable should not see every other value
 * jump — but stickiness has to end where the definition changes under it. Narrowing a range from
 * `10000 to 1e15` down to `1 to 10` left the row reporting 15,066: a number the variable could no
 * longer produce, sitting in the column the panel exists to let you trust.
 *
 * Still in range AND still on the step grid, so changing `step` from 0.5 to 1 drops a draw of
 * 5.5 as well. The grid test allows a float's worth of slack, since `min + k*step` rarely lands
 * exactly where arithmetic on the stored numbers does.
 */
function canStillDraw(v: { min: number; max: number; step: number }, n: number): boolean {
  if (n < v.min || n > v.max) return false;
  const steps = (n - v.min) / v.step;
  return Math.abs(steps - Math.round(steps)) < 1e-9;
}

/**
 * The strip's displayed values, resolved as a whole rather than row by row.
 *
 * It cannot be per-row any more: an equation's value is a function of the OTHER variables, so the
 * old `computeRoll(v)` had no way to compute one. It also hand-rolled both the draw and the
 * formatting, which is exactly how a panel and the blocks end up disagreeing about what a variable
 * is worth — everything here now comes from the one shared resolver.
 *
 * Keyed back to id because two variables may legitimately share a NAME across the two scopes while
 * the panel is mid-edit, and each row still has to render something.
 */
function sampleValues(vars: TaskVariable[], draws: Record<string, number>): {
  byId: Record<string, string>; byName: Record<string, string>;
} {
  const byName = resolveVariables(vars, {
    format: fmtResolved,
    // NaN, never a stand-in number. The draws are state and this runs during render, so on the one
    // frame between a variable appearing and the effect drawing for it there is no value yet —
    // NaN leaves its chip out for that frame, where `?? 0` would flash a zero that is not its
    // value. Too small a window for a test to catch, which is exactly why it is written down.
    sampleRandom: v => draws[v.id] ?? NaN,
  });
  // BOTH keyings, because two consumers need different ones and deriving the second at a call
  // site is how they drift: the strip renders per ROW so it needs ids, while the equation field
  // resolves `{name}` references so it needs names. Passing the id-keyed map to the field made
  // every equation preview read "Invalid expression" while the strip beside it showed the right
  // number — the resolver was never wrong, the key was.
  const byId: Record<string, string> = {};
  for (const v of vars) {
    const shown = byName[v.name];
    if (shown !== undefined) byId[v.id] = shown;
  }
  return { byId, byName };
}

// ─── useNumField ──────────────────────────────────────────────────────────────

// `external` is absent, not just NaN, for a variable that has no range at all — a constant or an
// equation. Both render as an empty field, so the two cases collapse here rather than at every
// call site.
function useNumField(
  external: number | undefined,
  fmt: (n: number) => string = String,
  /**
   * Narrow a typed number to what may actually be stored.
   *
   * It has to live HERE rather than at the call site. Clamping in the caller's `onCommit` left
   * the field showing what was typed while storing something else: type 103 into a field capped
   * at 10 and the box reads 103 while the value is 10, because the correction effect below only
   * fires when the EXTERNAL value changes, and 10 → 10 is no change. Clamping inside means the
   * blur that normalises the display and the value that gets stored are the same number by
   * construction.
   */
  clamp: (n: number) => number = n => n,
) {
  const shown = (n: number | undefined) => n == null || isNaN(n) ? '' : fmt(n);
  const [str, setStr] = useState(() => shown(external));
  const committed = useRef(external);

  useEffect(() => {
    if (committed.current !== external) {
      committed.current = external;
      setStr(shown(external));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [external]);

  function onChange(
    /** Already grammar-filtered and comma-normalised by NumberField. */
    value: string,
    onCommit: (n: number) => void,
  ) {
    const s = value;
    setStr(s);
    const n = parseFloat(s);
    if (!isNaN(n)) { const c = clamp(n); committed.current = c; onCommit(c); }
  }

  /** `onClear` makes BLANK a real value rather than a transient one — the equation's rounding is
   *  the first field where "none" is a thing a teacher can mean, and leaving it means it. */
  function onBlur(onCommit: (n: number) => void, onClear?: () => void) {
    const n = parseFloat(str);
    if (isNaN(n)) {
      if (onClear && str.trim() === '') { committed.current = undefined; onClear(); return; }
      setStr(shown(committed.current));
    } else {
      // The display settles on what was actually stored, which is what makes a clamp visible
      // rather than silent.
      const c = clamp(n);
      setStr(fmt(c));
      committed.current = c;
      onCommit(c);
    }
  }

  return { str, onChange, onBlur };
}

// ─── VariableRow ──────────────────────────────────────────────────────────────

interface VariableRowProps {
  variable: TaskVariable;
  allNames: string[];
  /** Called as the teacher types a name, so a refusal that was keyed to the old one can lift. */
  onNameEdited?: () => void;
  isArmed: boolean;
  /** Why Delete is refused, or false. The two reasons send a teacher looking in different
   *  places, so one boolean could only ever describe one of them. */
  usedBy: VariableUsage;
  onArm: () => void;
  onDisarm: () => void;
  onUpdate: (patch: Partial<Omit<TaskVariable, 'id'>>) => void;
  onRemove: () => void;
  /** Every variable in both scopes — what an equation may reference, before the cycle filter. */
  everyVariable: TaskVariable[];
  /** This row's own resolved value, already formatted. Absent when it does not resolve. */
  shownValue?: string;
  /** Whether the teacher has opened this row's definition. A row can also be open because it is
   *  not finished or not valid — see `open` in the body. */
  isExpanded: boolean;
  onToggle: () => void;
  onCollapse: () => void;
  /** Phone full-screen: the toggle needs a 44px target, which a 36px row does not give. */
  roomyTargets: boolean;
  /** A member of a set, drawn with the pathway elbow and showing only its leaf — the set names
   *  itself in the header above, so repeating it on every row is noise that costs width. */
  inSet: boolean;
  /** The sample values keyed by NAME — what the equation field resolves `{name}` against. */
  rolledByName: Record<string, string>;
}

function VariableRow({ variable, allNames, onNameEdited, isArmed, usedBy, onArm, onDisarm, onUpdate, onRemove, everyVariable, rolledByName, shownValue, isExpanded, onToggle, onCollapse, roomyTargets, inSet }: VariableRowProps) {
  const { t } = useTranslation();
  const kind = variableKind(variable);
  const minField  = useNumField(variable.min,  fmtField);
  const maxField  = useNumField(variable.max,  fmtField);
  const stepField = useNumField(variable.step, fmtField);
  const valueField = useNumField(variable.value, fmtField);
  // Blank is a real state here: it means the teacher has asked for no rounding.
  const decimalsField = useNumField(variable.decimals, String, clampDecimals);
  const sigFigsField  = useNumField(variable.sigFigs,  String, clampSigFigs);

  /*
   * WHICH ROUNDING an equation is using, read off which field is stored rather than from a flag.
   *
   * Same reasoning as `variableKind`: two field signatures cannot disagree with themselves the
   * way a flag sitting beside them can. `decimals` wins when both somehow exist, matching the
   * resolver, so the switch shows the rounding that is actually being applied.
   */
  const storedRoundingKind: 'decimals' | 'sigFigs' | null =
    variable.decimals != null ? 'decimals' : variable.sigFigs != null ? 'sigFigs' : null;
  /*
   * WITH NO NUMBER TYPED, the kind has nowhere to be stored — blank is a real value meaning "no
   * rounding asked for", and it is the same blank either way. So the preference is held here
   * until there is a number to attach it to.
   *
   * Without this the switch silently did nothing on an empty field: it wrote two `undefined`s,
   * the derivation read neither, and the label fell back to Decimals.
   */
  const [roundingPref, setRoundingPref] = useState<'decimals' | 'sigFigs'>('decimals');
  const roundingKind = storedRoundingKind ?? roundingPref;
  const roundingField = roundingKind === 'decimals' ? decimalsField : sigFigsField;

  const skewed = variable.skew != null && !isNaN(variable.skew);

  /*
   * WHETHER LINE THREE IS SHOWING is disclosure, not data — the one place the two legitimately
   * differ, because the slider's middle position clears the skew and the teacher is plainly
   * still using the control at that moment. Closing it is the button, which also clears.
   *
   * `|| skewed` so the data can always re-open it: an undo that restores a skew must not leave a
   * skewed variable with its only control hidden.
   */
  const [showSkewLine, setShowSkewLine] = useState(skewed);
  const showSkew = showSkewLine || skewed;

  /**
   * The slider's position: signed distance from the uniform middle.
   *
   * Negative is toward min, positive toward max, and the magnitude is how far past 50 the
   * strength is. Strengths of 1 to 50 do not exist — 50 IS uniform — so the position maps onto
   * 51..MAX_SKEW with no dead zone to drag through.
   */
  const skewSlider = !skewed ? 0
    : (variable.skewToward === 'max' ? 1 : -1) * (Math.min(variable.skew!, MAX_SKEW) - 50);

  /** Position back to stored fields. Zero is the uniform draw, which is stored as no skew at
   *  all rather than as a strength of 50 — one spelling, not two. */
  const skewFromSlider = (n: number): Partial<TaskVariable> => n === 0
    ? { skew: undefined, skewToward: undefined }
    : { skew: clampSkew(50 + Math.abs(n)), skewToward: n < 0 ? 'min' : 'max' };

  const skewReadout = !skewed
    ? t('task.settings.variables.skewNone')
    : `${t(variable.skewToward === 'max'
      ? 'task.settings.variables.skewTowardHigh'
      : 'task.settings.variables.skewTowardLow')} ${Math.min(variable.skew!, MAX_SKEW)}%`;

  /** A member edits its LEAF; the set supplies the rest when it is written back. */
  const shownName = inSet ? setLeaf(variable) : variable.name;
  const [nameDraft, setNameDraft] = useState(shownName);
  useEffect(() => { setNameDraft(shownName); }, [shownName]);

  // Validated against the DRAFT, not the committed name. A duplicate is now refused at commit, so
  // `variable.name` can never hold one — checking it would mean the error never appeared and the
  // rename just silently failed. Against the draft, the clash is flagged while it is being typed,
  // which is also the only moment the teacher can act on it.
  const isDup         = !!nameDraft && allNames.filter(n => n === nameDraft).length > (nameDraft === variable.name ? 1 : 0);
  const hasNameError  = !!nameDraft && (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(nameDraft) || isDup);
  const hasRangeError = kind === 'random' && variable.min != null && variable.max != null
    && !isNaN(variable.min) && !isNaN(variable.max) && variable.max <= variable.min;
  const hasStepError  = kind === 'random' && variable.step != null && !isNaN(variable.step) && variable.step <= 0;

  /*
   * A SKEW SO STRONG THE VARIABLE HAS STOPPED VARYING — a warning, not an error.
   *
   * Computed per range rather than read off the strength, because the strength alone does not
   * say: 85 leaves a 2493-point range drawing its minimum 16% of the time and a three-point
   * range drawing it 77% of the time. The cap on the field cannot express that; this can.
   *
   * It does not block saving and does not count as `broken`. The teacher has authored something
   * coherent that is almost certainly not what they meant, which is exactly what a warning is
   * for, and forcing the row open on it would fight someone deliberately building a near-fixed
   * value.
   */
  const degenerateSkew = kind === 'random' && isDegenerateSkew(variable);

  /*
   * CIRCULAR REFERENCES, caught at the second of two layers.
   *
   * The first is `cycleSafe` below: the formula field is handed a variable list with the loop
   * closers removed, so the chip picker cannot offer one and a typed `{name}` for one never
   * becomes a chip. That is prevention, and it is not enough on its own — a teacher can type
   * braces anyway, and a rename or a paste can point two formulas at each other without either
   * row being edited. This is the detection that has to exist alongside it.
   */
  const problem = kind === 'equation' ? equationProblem(variable, everyVariable) : null;
  /*
   * DOES IT PRODUCE A NUMBER? — the question `equationProblem` does not ask.
   *
   * A division by zero parses, names a defined variable and closes no cycle, so it passed all
   * three of its checks and was accepted in silence. Only asked once the cheap checks are
   * clean: sweeping the rolls of a formula that does not parse is wasted work, and the syntax
   * message is the more useful one to show anyway.
   */
  const evaluability = kind === 'equation' && !problem
    ? equationEvaluability(variable.formula ?? undefined, everyVariable, variable.angleMode ?? 'degrees')
    : 'ok';

  /* Naming WHICH kind of use blocks the delete. "Remove all {name} chips from blocks" sent a
     teacher hunting through questions for a chip that was never there, when the thing holding
     the variable was another variable's equation. */
  /*
   * WHERE, not just whether — and in words a teacher uses.
   *
   * It said "Remove all {name} chips from blocks before deleting". "Chips" is our word for them,
   * not theirs, and "from blocks" sent them hunting through every question in the task. It now
   * names the places, up to two, and counts the rest: naming all of them is not feasible once a
   * variable can reach every task in an experience, and a wall of names answers nothing anyway.
   */
  const blockedReason = usedBy === false ? undefined
    // Braced, because that is how a teacher sees a variable everywhere else.
    : usedBy.kind === 'formula'
      ? t('task.settings.variables.deleteBlockedByEquation', { dependent: `{${usedBy.dependent}}` })
      : t('task.settings.variables.deleteBlockedWhere', {
        places: usedBy.more === 0
          ? usedBy.names.join(', ')
          : t(usedBy.kind === 'tasks'
            ? 'task.settings.variables.deleteBlockedMoreTasks'
            : 'task.settings.variables.deleteBlockedMorePlaces',
          { list: usedBy.names.join(', '), count: usedBy.more }),
      });

  const nameErrId  = `${variable.id}-name-err`;
  const rangeErrId = `${variable.id}-range-err`;
  const stepErrId  = `${variable.id}-step-err`;
  const skewWarnId = `${variable.id}-skew-warn`;
  const formulaErrId = `${variable.id}-formula-err`;
  /** The region the summary button opens — `aria-controls` needs it to exist by name. */
  const defId = `${variable.id}-definition`;

  /* Escape shuts the definition, and lives on the REGION rather than on the row: a key handler
     on a role-less div is a control a keyboard cannot reach, which is what the a11y lint says.
     Only a row the teacher opened closes — one held open because it is unfinished or broken has
     nothing to close to. */
  const onDefinitionKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && isExpanded) { e.stopPropagation(); onCollapse(); }
  };

  /** What this equation may reference: everything except itself and anything built on it. */
  const cycleSafe = useMemo(() => {
    if (kind !== 'equation') return everyVariable;
    const forbidden = wouldCycle(variable.name, everyVariable);
    return everyVariable.filter(v => !forbidden.has(v.name));
  }, [kind, variable.name, everyVariable]);


  /*
   * A ROW THAT IS NOT FINISHED, OR NOT VALID, CANNOT HIDE ITS DEFINITION.
   *
   * One rule covers three things that would otherwise each need their own: a newly added row
   * opens (it has nothing in it yet), a broken one cannot be collapsed out of sight (an
   * unreachable error is worse than no error), and a half-filled one cannot masquerade as a
   * finished variable whose value merely has not rolled — its value column is empty either way,
   * so the open definition is the only thing telling the two apart.
   *
   * It is also what keeps the accordion from fighting the teacher: adding a second variable
   * cannot close the first, because the first is not finished either.
   */
  const unfinished = !isSavableVariable(variable);
  const broken = !!problem || hasRangeError || hasStepError;
  const open = kind !== 'constant' && (isExpanded || unfinished || broken);

  /**
   * The one line a collapsed row gets to describe itself.
   *
   * The DEFINITION, not a gloss of it: a range reads as its two ends, an equation as the
   * expression itself with the braces taken off — `kayak_speed * time`. Braces are stripped
   * because a `{name}` is a chip or a resolved number everywhere in this app and never literal
   * text, and a chip is more than 88px can carry.
   *
   * A constant has none: its value IS its definition, and that is already in the column to the
   * right, editable.
   */
  const summary: React.ReactNode = kind === 'random'
    ? (variable.min != null && variable.max != null && !isNaN(variable.min) && !isNaN(variable.max)
      /*
       * THE SEPARATOR IS THE INDICATOR, when the draw is skewed.
       *
       * A histogram glyph after the text failed twice over: it cost 14px of 86 in a column that
       * already truncates `2055 to 3055`, and at a strong skew its own silhouette collapses to
       * one bar, which beside digits reads as a `1`.
       *
       * An arrow replacing the word costs NEGATIVE width — it is narrower than " to " — and is
       * the same shape at every strength. It points at the end the values bunch toward, which is
       * the one thing the closed row has to say.
       */
      ? (skewed
        ? <>{fmtField(variable.min)} <span className='text-[15px] leading-none text-ws-text-link'>{variable.skewToward === 'max' ? '\u25B8' : '\u25C2'}</span> {fmtField(variable.max)}</>
        : t('task.settings.variables.summaryRange', { min: fmtField(variable.min), max: fmtField(variable.max) }))
      : '')
    : summariseFormula(variable.formula ?? '', inSet ? variable.set : undefined);

  /**
   * The door to line three, defined once and rendered in one of two slots.
   *
   * Two call sites rather than two buttons: a second copy would be a second place to forget
   * that closing has to CLEAR the skew, which is the part that matters.
   */
  const skewDoor = (
    <IconButton
      size={roomyTargets ? 'lg' : 'sm'}
      // Sets aria-pressed too. A FILLED state rather than a tint: at 28px square, the tinted
      // on/off grammar the panel toggles use was not readable as two different things.
      isActive={showSkew}
      aria-label={t('task.settings.variables.skewLabel')}
      onClick={() => {
        // Closing CLEARS: a skew left in place with its only control hidden is a variable
        // quietly drawing unevenly with nothing on screen saying so.
        if (showSkew) { setShowSkewLine(false); if (skewed) onUpdate({ skew: undefined, skewToward: undefined }); }
        else { setShowSkewLine(true); onUpdate({ skew: DEFAULT_SKEW, skewToward: 'min' }); }
      }}>
      {/* The icon is the distribution itself — level bars when the draw is uniform, leaning ones
          when it is not. So the glyph says WHICH WAY and roughly how hard without being opened,
          and the off state is not just a paler version of the on state. */}
      <SkewPreview variable={variable} className='h-3.5 w-4' />
    </IconButton>
  );

  /** One field plus its own label, which line two now has room for. */
  const labelled = (label: string, node: React.ReactNode) => (
    <span className='flex min-w-0 items-center gap-1'>
      <span className='shrink-0 text-xs text-ws-muted'>{label}</span>
      {node}
    </span>
  );

  return (
    <div>
      <div aria-live='assertive' aria-atomic='true' className='sr-only'>
        {isArmed ? t('blocks.actions.deleteArmed') : ''}
      </div>
      {/* Only the OPEN row carries a background, so it reads as a card that came out of the list.
          A TINT OF THE TEAL, not grey: grey reads as disabled, and the teal is already the row's
          own colour in the value column — the same `ws-text-link` at 10% that the variable chips
          are filled with, so an opened variable and a variable chip look like the same family. */}
      <div
        className={`-mx-2 flex flex-col gap-1 rounded-md px-2 py-1 transition-colors ${
          isArmed
            ? `${ARMED_DELETE_PAINT}`
            : open ? 'bg-ws-text-link/[.08] dark:bg-ws-text-link/[.14]' : ''
        }`}>

        {/*
         * LINE ONE IS THE SAME FOR ALL THREE TYPES: what it is called, and what it is worth.
         *
         * The value sits in a fixed column at the right so it can be read straight down the
         * panel, which is the thing the bottom "Sample values" strip used to do badly — a random
         * showed its value down there while an equation showed it inline, so the two never lined
         * up and neither was where you were looking.
         *
         * For a CONSTANT that column holds the editable field itself, because what a constant is
         * worth is exactly what you type into it. Same column, same question, different answer.
         */}
        <div className='flex items-center gap-1.5'>
          <span title={t(TYPE_NAME_KEY[kind])} className='flex w-4 shrink-0 justify-center text-ws-muted'>
            <KindGlyph kind={kind} />
            <span className='sr-only'>{t(TYPE_NAME_KEY[kind])}</span>
          </span>

          {/* Committed on blur/Enter, NOT per keystroke. Renaming a variable rewrites every chip
              that references it across every block, and a per-keystroke commit meant each PREFIX
              of a name was applied as a real rename: typing "test2" into a new variable while a
              `test` already existed ran a `test` -> `test2` sweep on the second-to-last character
              and rewrote the other variable's chips everywhere. A draft holds the text until it
              is actually meant. */}
          {/* `prose`, not `literal`. Literal means MONOSPACE, and it is there so lookalikes show —
              `l` against `I` against `1` — in content matched verbatim against a student's
              answer. A variable name is never matched against anything: it is picked from a chip
              menu, not retyped. Monospace was costing about 18px for twelve characters, which is
              most of what the value column was short of. */}
          <TextField
            variant='prose'
            value={nameDraft}
            // The identifier grammar — letters, digits, underscore — so a name is safe to embed
            // in {name} and in a formula. Kept at the call site: it is the only field with this
            // grammar, and it belongs to what a VARIABLE is rather than to text entry.
            onChange={v => { setNameDraft(v.replace(/[^a-zA-Z0-9_]/g, '')); onNameEdited?.(); }}
            /*
             * COMING BACK TO THE FIELD IS ASKING AGAIN.
             *
             * A refusal used to last the whole session, so a teacher who dismissed the dialog to
             * go and check something could not get it back without reloading — they had decided
             * to promote and could not. Clearing on EDIT alone was not enough either: after a
             * refusal the field already holds the refused name, so there is nothing to edit, and
             * retyping the same characters is not a change. Focus is the moment they come back.
             */
            onFocus={() => onNameEdited?.()}
            onBlur={() => {
              if (nameDraft === shownName || hasNameError || !nameDraft) return;
              // A member's stored name stays the COMPOSED one — the leaf is only what the row
              // shows, so everything downstream still sees `starA_mass`.
              onUpdate({ name: inSet && variable.set ? composeSetName(variable.set, nameDraft) : nameDraft });
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
              if (e.key === 'Escape') { e.preventDefault(); setNameDraft(shownName); }
            }}
            hasError={hasNameError}
            describedBy={hasNameError ? nameErrId : undefined}
            maxLength={12}
            ariaLabel={t('task.settings.variables.nameLabel')}
            className='min-w-0 flex-1' />

          {/*
            * THE DISCLOSURE, and it has to LOOK like one.
            *
            * This was grey text with no border, on the reasoning that the summary could be both
            * the description and the control and save the width an affordance would cost. The
            * owner tried it: "it is not obvious that clicking on a greyed out -10 to 10 would
            * open an editor." Correct, and the same lesson as the Add buttons — a control nobody
            * recognises is a feature that does not exist, so the width is not optional.
            *
            * A CARET, and no box. A bordered field with a chevron-down is what this app's select
            * menus look like, which is what the first attempt at an affordance accidentally
            * built: "it looks like a dropdown menu rather than an expander." The expander
            * convention here is the one the feedback cards and flow change cards already use —
            * `ChevronRight` turning to `ChevronDown`, bare, with the panel opening beneath.
            *
            * The text truncates to pay for the caret, which is the right thing to give up: it is
            * a reminder of a definition one click away, where the name and the value are not.
            */}
          {kind !== 'constant' && (
            <button
              type='button'
              onClick={onToggle}
              aria-expanded={open}
              aria-controls={defId}
              /*
                * The skew goes in the NAME, not just the glyph beside it.
                *
                * An `aria-label` replaces the button's contents outright, so an `sr-only` span
                * inside it would never be read — the only way a screen reader hears that this
                * variable draws unevenly is for the label itself to say so.
                */
              aria-label={
                t('task.settings.variables.editDefinition', { name: variable.name || t('task.settings.variables.unnamedFallback') })
                + (kind === 'random' && skewed ? `, ${skewReadout}` : '')
              }
              className={`flex w-[104px] shrink-0 items-center gap-0.5 rounded px-0.5 text-xs transition-colors hover:bg-ws-hover ${
                roomyTargets ? 'min-h-11' : 'min-h-7'
              } ${open ? 'text-ws-text' : 'text-ws-muted hover:text-ws-text'}`}>
              <span className='min-w-0 flex-1 truncate text-left'>{summary}</span>
              {/* After the summary, not before: the control follows the thing it acts on. */}
              {open
                ? <ChevronDown aria-hidden='true' className='h-3.5 w-3.5 shrink-0' />
                : <ChevronRight aria-hidden='true' className='h-3.5 w-3.5 shrink-0' />}
            </button>
          )}

          {/* A constant keeps the summary column as empty space rather than absorbing it, so
              every name is the same width and every value lines up down the panel whatever the
              type. Alignment is the whole point of the column, so this width TRACKS the button's
              above: 104px, widened from 86 because `2055 to 3055` did not fit in 86 even before
              anything was added to it. The name column pays, and it has the room. */}
          {kind === 'constant' && <span className='w-[104px] shrink-0' aria-hidden='true' />}

          {kind === 'constant'
            ? (
              <NumberField
                grammar='scientific'
                allowNegative
                value={valueField.str}
                onChange={v => valueField.onChange(v, n => onUpdate({ value: n }))}
                onBlur={() => valueField.onBlur(n => onUpdate({ value: n }))}
                ariaLabel={t('task.settings.variables.valueLabel')}
                /* Right-aligned to match the readouts below it — digits in one column line up
                   only if the editable one agrees. The variant reaches the inner input, which
                   `className` alone does not: it lands on NumberField's wrapper. */
                className='w-[84px] shrink-0 [&_input]:text-right' />
            )
            : (
              // Not a field, so it reads as a readout rather than something to fill in. A row
              // that cannot resolve shows nothing here — the reason is already stated underneath.
              // Bigger and teal, because it is the thing the panel exists to let you check. The
              // token is `ws-text-link`, not `ws-primary` — the one that keeps its contrast on
              // both grounds, which is why chips and highlights use it everywhere else.
              /*
               * Width is the CEILING, not a guess: `fmtValue` caps at six significant digits and
               * switches to scientific outside 1e-5..1e5, so the longest string it can emit is
               * `-1.23457e-9`. The column was 72px and clipped `0.333333` and everything
               * scientific.
               *
               * No `=`. The column position and the one teal thing on the row already say what
               * this is, so the glyph was costing 14px to repeat them. A SCREEN READER has
               * neither cue and would hear a bare number after the summary's numbers, so it
               * keeps the word.
               *
               * `title`, because an UNROUNDED value is the full number now and can outrun
               * the column: 310/3 is 103.333333333333. Clipping it is a fair nudge towards
               * setting Decimals, but the teacher still has to be able to read what it is.
               */
              <span title={shownValue ?? undefined}
                className='w-[84px] shrink-0 truncate pr-2 text-right text-sm font-medium tabular-nums text-ws-text-link'>
                {shownValue != null && <span className='sr-only'>{t('task.settings.variables.valueLabel')} </span>}
                {shownValue ?? ''}
              </span>
            )}

          <IconButton
            variant='destructive'
            size='sm'
            isArmed={isArmed}
            disabled={!!usedBy}
            title={blockedReason}
            onClick={isArmed ? onRemove : onArm}
            onPointerLeave={(e) => { if (e.pointerType !== 'touch') onDisarm(); }}
            onBlur={onDisarm}
            aria-label={blockedReason
              ?? (isArmed
                ? t('task.settings.variables.deleteConfirm', { name: variable.name || t('task.settings.variables.unnamedFallback') })
                : t('task.settings.variables.deleteLabel',   { name: variable.name || t('task.settings.variables.unnamedFallback') }))}
          >
            <Trash2 aria-hidden='true' className='h-3.5 w-3.5' />
          </IconButton>
        </div>

        {/* LINE TWO — how the value is arrived at. A constant has none: line one already said it.
            Indented past the glyph so the definition reads as belonging to the name above it. */}
        {open && kind === 'random' && (
          // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- not a control: it catches Escape BUBBLING from the real fields inside it; the toggle is a real button with aria-expanded
          <div id={defId} role='group' onKeyDown={onDefinitionKeyDown}
            aria-label={t('task.settings.variables.editDefinition', { name: variable.name || t('task.settings.variables.unnamedFallback') })}
            className='flex items-center gap-1.5 pl-[22px]'>
            {labelled(t('task.settings.variables.minLabel'), (
              <NumberField
                grammar='scientific'
                allowNegative
                value={minField.str}
                onChange={v => minField.onChange(v, n => onUpdate({ min: n }))}
                onBlur={() => minField.onBlur(n => onUpdate({ min: n }))}
                hasError={hasRangeError}
                describedBy={hasRangeError ? rangeErrId : undefined}
                ariaLabel={t('task.settings.variables.minLabel')}
                className='min-w-0 flex-1' />
            ))}
            {labelled(t('task.settings.variables.maxLabel'), (
              <NumberField
                grammar='scientific'
                allowNegative
                value={maxField.str}
                onChange={v => maxField.onChange(v, n => onUpdate({ max: n }))}
                onBlur={() => maxField.onBlur(n => onUpdate({ max: n }))}
                hasError={hasRangeError}
                describedBy={hasRangeError ? rangeErrId : undefined}
                ariaLabel={t('task.settings.variables.maxLabel')}
                className='min-w-0 flex-1' />
            ))}
            {labelled(t('task.settings.variables.precisionLabel'), (
              <NumberField
                grammar='scientific'
                value={stepField.str}
                onChange={v => stepField.onChange(v, n => onUpdate({ step: n }))}
                onBlur={() => stepField.onBlur(n => onUpdate({ step: n }))}
                hasError={hasStepError}
                describedBy={hasStepError ? stepErrId : undefined}
                ariaLabel={t('task.settings.variables.precisionLabel')}
                className='min-w-0 flex-1' />
            ))}
            {/*
              * A FOURTH CONTROL WOULD NOT FIT, so this is a door rather than a control.
              *
              * Line two has roughly 312px and three labelled number fields already in it; adding
              * direction and strength inline took all five below what a number can be read in.
              * Pressing this writes the default skew, which is what makes line three appear —
              * there is no "opened but unset" state, so the control and the data cannot disagree
              * about whether this variable is skewed.
              */}
            {skewDoor}
          </div>
        )}

        {/*
          * LINE THREE — one slider, through a neutral middle.
          *
          * ONE control for two things, because they are not two decisions: a direction with no
          * strength and a strength with no direction are both meaningless, and a pair of
          * controls makes a teacher set them separately and lets them contradict. Sliding left
          * and right from the middle is the direction; distance from the middle is the strength.
          *
          * It also gives "none" a POSITION. A two-way switch has no off, so the uniform draw
          * would have been reachable only by leaving the line entirely — here it is the middle
          * of the thing you are already dragging, and it is where the slider starts.
          *
          * The readout is fixed-width and the whole line never reflows, so dragging does not
          * move the slider out from under the pointer.
          */}
        {open && kind === 'random' && showSkew && (
          <div className='flex items-center gap-2 pl-[22px]'>
            <RangeField
              value={skewSlider}
              onChange={n => onUpdate(skewFromSlider(n))}
              min={-(MAX_SKEW - 50)}
              max={MAX_SKEW - 50}
              ariaLabel={t('task.settings.variables.skewLabel')}
              // The readout, and what a screen reader hears: the raw offset is an implementation
              // detail, and "-30" says nothing a teacher set.
              valueText={skewReadout}
              describedBy={degenerateSkew ? skewWarnId : undefined}
              fieldId='skew'
              roomy={roomyTargets}
              className='flex-1' />
          </div>
        )}

        {/* The SAME equation editor every block's answer uses, not a second one: chip picker,
            `{name}` auto-chipping and the shared angle-aware scope are already what this needs.
            Its variable list is the cycle-safe one, which is what makes a circular reference
            unofferable. `hideEval` because the result is on line one now, in the column every
            other type reports into. */}
        {open && kind === 'equation' && (
          // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- not a control: it catches Escape BUBBLING from the real fields inside it; the toggle is a real button with aria-expanded
          <div id={defId} role='group' onKeyDown={onDefinitionKeyDown}
            aria-label={t('task.settings.variables.editDefinition', { name: variable.name || t('task.settings.variables.unnamedFallback') })}
            className='pl-[22px]'>
            {/* CENTRED, not bottom-aligned: the equation field wraps to two and three lines as a
                formula grows, and an end-aligned Decimals box slid down the side with it. */}
            <div className='flex items-center gap-1.5'>
              <FormulaField
                value={variable.formula ?? ''}
                onChange={formula => onUpdate({ formula })}
                angleMode={variable.angleMode ?? 'degrees'}
                onAngleModeChange={angleMode => onUpdate({ angleMode })}
                variables={cycleSafe}
                rolledValues={rolledByName}
                hasError={!!problem || evaluability === 'never'}
                ariaDescribedBy={problem || evaluability !== 'ok' ? formulaErrId : undefined}
                ariaLabel={t('task.settings.variables.equationLabel')}
                hideEval
                /* A variable is rolled when the TASK starts, before any answer exists to
                   recall — see `noAnswerRefs`. */
                noAnswerRefs
                className='min-w-0 flex-1' />

              {/*
                * HOW PRECISE THE RESULT IS, not how precisely it is shown.
                *
                * Rounding happens during resolution, so this decides the number the next equation
                * reads and the grader uses — a value displayed as 1.77e9 while secretly being
                * used as 1.7677669e9 is the error the whole rule exists to prevent.
                *
                * DECIMALS, calling mathjs's own `round(value, decimals)` — the same function the
                * teacher can type into any formula field here, so the control and the hand-written
                * call cannot drift. Significant figures were tried first and read wrong: it
                * implies 34 should render as 34.00000.
                *
                * BLANK is a real value and the default: no rounding asked for, which resolves at
                * full double precision. It is not a slip to be filled in.
                */}
              {/* Label ABOVE its box, not beside it: inline it cost the equation field about 45px
                  of the little it has, and the equation is the thing being read.

                  THE LABEL IS THE SWITCH, the same move as the degrees/radians toggle inside the
                  formula field: two kinds of rounding, one of them active, and naming the active
                  one is already the label's job. A separate control beside it would have cost
                  width this row does not have to say something the label was saying anyway.

                  The two are alternatives, not a pair — `fieldsForRounding` clears the one being
                  left, because a surviving `decimals` outranks the `sigFigs` just chosen. */}
              {/* FIXED WIDTH, because the label is also the control: "Decimals" and "Sig figs"
                  do not set the same width, so a column sized to its contents made the equation
                  field jump every time the kind was switched. 68px holds the wider of the two,
                  and the box is centred under it rather than left against its edge. */}
              <span className='flex w-[68px] shrink-0 flex-col items-center gap-1'>
                <button
                  type='button'
                  onClick={() => {
                    const next = roundingKind === 'decimals' ? 'sigFigs' as const : 'decimals' as const;
                    setRoundingPref(next);
                    onUpdate(fieldsForRounding(
                      next,
                    // The typed number carries across, re-clamped: 3 decimals and 3 figures are
                    // both legal, and dropping it would make the switch feel like a reset.
                      roundingField.str === '' ? undefined
                        : next === 'sigFigs'
                          ? clampSigFigs(Number(roundingField.str))
                          : clampDecimals(Number(roundingField.str)),
                    ));
                  }}
                  aria-label={t(roundingKind === 'decimals'
                    ? 'task.settings.variables.roundingSwitchToSigFigs'
                    : 'task.settings.variables.roundingSwitchToDecimals')}
                  /*
                   * A GLYPH, not just a dotted underline.
                   *
                   * The underline alone sat two pixels above the field's own top border and read
                   * as part of it, so the one thing marking the label as pressable looked like
                   * chrome belonging to the box below. The arrows say "these two swap" without
                   * depending on a line that close to another line; `gap-1` above gives the
                   * underline enough room to be its own mark again.
                   */
                  /* `py-2 -my-2`: a 10px line of text is a 10px click target, well under the 24px
                     a pointer needs. The padding grows the hit box and the negative margin takes
                     the growth back out of the layout, so nothing below it moves. It reaches 8px
                     into the 11px gap above the field and so never steals the field's own clicks. */
                  className='-my-2 flex w-full items-center justify-center gap-0.5 rounded px-1 py-2 text-[10px] leading-none text-ws-muted hover:text-ws-text-link'>
                  <ArrowLeftRight aria-hidden='true' className='h-2.5 w-2.5 shrink-0' />
                  <span className='underline decoration-dotted underline-offset-2'>
                    {t(roundingKind === 'decimals'
                      ? 'task.settings.variables.decimalsLabel'
                      : 'task.settings.variables.sigFigsLabel')}
                  </span>
                </button>
                <NumberField
                  grammar='integer'
                  value={roundingField.str}
                  onChange={v => roundingField.onChange(v, n => onUpdate(fieldsForRounding(roundingKind, n)))}
                  onBlur={() => roundingField.onBlur(
                    n => onUpdate(fieldsForRounding(roundingKind, n)),
                    // Blank means no rounding asked for, which is a value, not a slip.
                    () => onUpdate(fieldsForRounding(roundingKind, undefined)),
                  )}
                  ariaLabel={t(roundingKind === 'decimals'
                    ? 'task.settings.variables.decimalsLabel'
                    : 'task.settings.variables.sigFigsLabel')}
                  className='w-12' />
              </span>
            </div>
          </div>
        )}
      </div>

      {(hasNameError || hasRangeError || hasStepError || problem || degenerateSkew
        || evaluability === 'never' || evaluability === 'sometimes') && (
        <div className='mt-0.5 flex flex-col gap-0.5 pl-[30px]'>
          {hasNameError  && <p id={nameErrId}  className='text-xs text-ws-error-fg'>{isDup ? t('task.settings.variables.nameDuplicate') : t('task.settings.variables.nameInvalid')}</p>}
          {problem && <p id={formulaErrId} className='text-xs text-ws-error-fg'>
            {problem.kind === 'syntax'
              ? t('task.settings.variables.equationSyntax')
              : problem.kind === 'cycle'
                ? t('task.settings.variables.equationCircular')
                : t('task.settings.variables.equationUnknown', { names: problem.names.map(n => `{${n}}`).join(', ') })}
          </p>}
          {/* `never` is a defect and `sometimes` a warning, the same split the blocks use for
              a claim quantified over rolls. `unknown` says nothing: the sweep declined, and a
              guess reported as a fact is worse than silence. */}
          {evaluability === 'never' && <p id={formulaErrId} className='text-xs text-ws-error-fg'>
            {t('task.settings.variables.equationNoValue')}
          </p>}
          {evaluability === 'sometimes' && <p id={formulaErrId} className='text-xs text-ws-warning-fg'>
            {t('task.settings.variables.equationNoValueSometimes')}
          </p>}
          {hasRangeError && <p id={rangeErrId} className='text-xs text-ws-error-fg'>{t('task.settings.variables.rangeInvalid')}</p>}
          {hasStepError  && <p id={stepErrId}  className='text-xs text-ws-error-fg'>{t('task.settings.variables.stepInvalid')}</p>}
          {degenerateSkew && <p id={skewWarnId} className='text-xs text-ws-warning-fg'>
            {t('task.settings.variables.skewDegenerate', {
              value: fmtField(skewModeValue(variable as Parameters<typeof skewModeValue>[0])),
            })}
          </p>}
        </div>
      )}
    </div>
  );
}

// ─── VariablesPanel ────────────────────────────────────────────────────────

/**
 * One scope's worth of rows: heading, table, add button, empty hint.
 *
 * Extracted so the two scopes cannot drift. They are the same control with a different list
 * behind them, and the alternative — rendering the markup twice — is how a feature grows two
 * versions of its own delete confirmation, which this repo has already paid for once.
 *
 * `allNames` deliberately spans BOTH scopes: a name has to be unique across the two, since
 * resolution is by name and nothing downstream could tell two definitions apart.
 */
function VariableScopeSection({
  id, heading, scope, variables, allNames, onNameEdited, usage, armedId, onArm, onDisarm,
  onAdd, onDeleteSet, onDuplicateSet, onUpdate, onRenameSet, onRemove, everyVariable, rolls, rolledByName, expandedId, onExpand, roomyTargets,
  onReroll, hasRollable, divider,
}: {
  id: string;
  heading: string;
  /** "(all tasks)" / "(this task)" — real information, but subordinate to the heading. */
  scope: string;
  variables: TaskVariable[];
  allNames: string[];
  onNameEdited?: () => void;
  usage: Record<string, VariableUsage>;
  /** Both scopes, for the equation picker and its cycle filter. */
  everyVariable: TaskVariable[];
  /** Resolved values keyed by ROW ID — each row's own, for the value column on its first line.
   *  By id rather than by name because two rows can share a name while one is being retyped. */
  rolls: Record<string, string>;
  /** The same values keyed by NAME, which is what an equation resolves `{name}` against. */
  rolledByName: Record<string, string>;
  armedId: string | null;
  onArm: (id: string) => void;
  onDisarm: () => void;
  /** At most one definition open across the WHOLE panel, which is what keeps it scannable. */
  expandedId: string | null;
  onExpand: (id: string | null) => void;
  roomyTargets: boolean;
  /** Redraws THIS scope's randoms only. The two scopes roll separately at runtime — a task's
   *  against the task, an experience's against `EXPERIENCE_ROLL_SCOPE` — so a reroll per section
   *  is what the data model already says, not just where the button fits. */
  onReroll: () => void;
  /** No reroll where there is nothing to roll: a control that visibly does nothing. */
  hasRollable: boolean;
  /** The rule above the heading, separating this section from the one before it. */
  divider: boolean;
  /** Which of the three to create, and into which set if any — a row is born with its type and
   *  its membership, not switched into either. */
  onAdd?: (kind: VariableKind, set?: string) => void;
  /** Add a variable that is already complete — what a duplicate produces. */
  /** Remove a whole set at once. It cannot be N variable deletions: the per-variable guard
   *  refuses a member another member depends on, which would make a set deletable only in
   *  reverse dependency order. */
  /** The WHOLE set, as one operation — never one call per member. See `handleDeleteSet`. */
  onDeleteSet: (set: string) => void;
  onDuplicateSet: (set: string) => void;
  onUpdate: (id: string, patch: Partial<Omit<TaskVariable, 'id'>>) => void;
  onRenameSet: (from: string, to: string) => void;
  onRemove: (id: string) => void;
}) {
  const { t } = useTranslation();
  const addBtnRef = useRef<HTMLButtonElement>(null);
  /** Sets the teacher has started that hold nothing yet. They are not stored — an empty set has
   *  no member to carry its name — so they live exactly as long as the panel does. */
  const [draftSets, setDraftSets] = useState<string[]>([]);
  /*
   * SETS START COLLAPSED, and the state tracks what is OPEN rather than what is shut.
   *
   * Both halves of one complaint: "if I collapse them and then return back later, they're all
   * expanded again, which defeats the purpose of having a collapse option." With the state
   * holding the CLOSED ones, a set that did not exist when the panel last rendered is open by
   * default, and the list resets on every remount — so every visit reopened everything.
   *
   * Collapsed is also the better default on its own: the panel is opened to review what has been
   * set up, and a closed set states its name and its members on one line.
   */
  const [expandedSets, setExpandedSets] = useState<string[]>([]);

  /** Constants (the givens), then randoms, then the equations built on them. Stable within each,
   *  so a row never moves because of an edit to a different one. */
  const byKind = (list: TaskVariable[]) => KIND_ORDER.flatMap(k => list.filter(v => variableKind(v) === k));
  const loose = byKind(variables.filter(v => !v.set));
  /** Stored sets, plus any the teacher has just started that has nothing in it yet. */
  const sets = [...setsIn(variables), ...draftSets.filter(d => !setsIn(variables).includes(d))];

  /**
   * ONE call, not one per member.
   *
   * This used to loop `onUpdate` over the members, on the reasoning that a set rename is "just"
   * its members renamed. It is not: in the experience scope each call captured the variable list
   * before its await and wrote it after, so only the last member actually moved, and each
   * planned its cross-task writes against the same revision, so the rest came back 409. The
   * teacher got a refusal and a second set holding one member — every attempt.
   *
   * An empty set has no member to carry its name and so exists only here, in `draftSets`; that
   * one really is renamed by changing the draft and nothing else.
   */
  const renameSet = (from: string, to: string, members: TaskVariable[]) => {
    if (!to || to === from) return;
    if (members.length > 0) onRenameSet(from, to);
    setDraftSets(prev => prev.map(d => (d === from ? to : d)));
  };

  /**
   * Why each set cannot go, worked out from what the page already computed.
   *
   * A member used by a SIBLING is not a reason: they leave together. Only a dependent left
   * behind counts — a block still showing a member, or an equation outside the set standing on
   * one — which is the same rule `setDeleteBlocker` states for the pure case.
   */
  /*
   * A ROW OPEN BECAUSE IT IS UNFINISHED TAKES THE ACCORDION SLOT.
   *
   * Without this it closes the moment it stops being unfinished, which is the moment the
   * teacher finishes typing — reported exactly that way: "as soon as I enter a variable in the
   * equation, it collapses as if it's finished." It IS finished; that is not a reason to shut
   * it while they are still looking at it.
   *
   * Claimed only when nothing else holds the slot, so two half-written rows cannot fight over
   * it — they are both open on their own account anyway, and the slot only decides what happens
   * when one of them is completed.
   */
  const needsSlot = variables.find(v => variableKind(v) !== 'constant'
    && (!isSavableVariable(v) || !!equationProblem(v, everyVariable)));
  const needsSlotId = needsSlot?.id;
  useEffect(() => {
    if (expandedId == null && needsSlotId) onExpand(needsSlotId);
  }, [expandedId, needsSlotId, onExpand]);

  /*
   * AND OPEN THE SET IT IS IN, or the row above takes the accordion slot while not being on the
   * screen at all.
   *
   * Sets default to collapsed, which is right for reviewing and wrong for a set with something
   * broken inside it: the card reads as fine, the error text is unrendered, and the one open
   * slot has been spent on a row nobody can see.
   *
   * Only when the problem APPEARS, keyed on the set name — so a teacher who opens it, sees the
   * error and collapses it anyway is not fought by the panel.
   */
  const needsSlotSet = needsSlot?.set;
  useEffect(() => {
    if (needsSlotSet) setExpandedSets(prev => prev.includes(needsSlotSet) ? prev : [...prev, needsSlotSet]);
  }, [needsSlotSet]);

  const setBlockers = Object.fromEntries(sets.map(set => {
    const members = variables.filter(v => v.set === set);
    const names = new Set(members.map(m => m.name));
    const inBlocks = members.find(m => {
      const u = usage[m.id];
      return u !== false && u !== undefined && (u.kind === 'places' || u.kind === 'tasks');
    });
    if (inBlocks) return [set, { reason: 'blocks' as const, name: inBlocks.name }];
    const outsider = everyVariable.find(o =>
      !names.has(o.name) && o.formula && formulaDependencies(o.formula).some(d => names.has(d)));
    return [set, outsider ? { reason: 'formula' as const, name: outsider.name } : false];
  })) as Record<string, { reason: 'blocks' | 'formula'; name: string } | false>;

  const row = (v: TaskVariable, inSet: boolean) => (
    <VariableRow
      key={v.id}
      variable={v}
      inSet={inSet}
      allNames={allNames}
      onNameEdited={onNameEdited}
      isArmed={armedId === v.id}
      usedBy={usage[v.id] ?? false}
      everyVariable={everyVariable}
      shownValue={rolls[v.id]}
      rolledByName={rolledByName}
      isExpanded={expandedId === v.id}
      onToggle={() => onExpand(expandedId === v.id ? null : v.id)}
      onCollapse={() => onExpand(null)}
      roomyTargets={roomyTargets}
      onArm={() => onArm(v.id)}
      onDisarm={onDisarm}
      onUpdate={patch => onUpdate(v.id, patch)}
      onRemove={() => {
        onRemove(v.id);
        onDisarm();
        requestAnimationFrame(() => addBtnRef.current?.focus());
      }}
    />
  );

  return (
    /* THE RULE IS ON THE SECTION, not on the heading, because the heading is sticky: a divider
       travelling with a pinned heading sits under the panel's own header with nothing above it
       to divide. Here it stays at the section's top edge and scrolls away with the rows. */
    <section aria-labelledby={`${id}-heading`}
      className={`flex flex-col gap-3 ${divider ? 'border-t border-ws-border' : ''}`}>
      {/*
        * `EditorSectionHeading`, the same one every block editor's QUESTION and ANSWER SETUP use,
        * rather than the smaller hand-rolled heading that was here — which was both a fork of a
        * shared component and visibly the wrong size next to it.
        *
        * Its `action` slot is described as furniture of the heading, "something a teacher needs
        * available but should not be led to", which is exactly a reroll. The scope qualifier is
        * set back rather than dropped: which tasks a variable reaches is the whole distinction
        * between the two sections, but at the heading's weight it competed with it.
        */}
      <EditorSectionHeading
        id={`${id}-heading`}
        // Long lists scroll past both scopes; the heading is what says which one you are in, and
        // Reroll is its furniture, so neither should leave with the rows. The divider goes on the
        // section above, for the reason stated there.
        sticky
        label={<>{heading}<span className='ml-1.5 font-normal normal-case tracking-normal text-ws-muted/70'>{scope}</span></>}
        action={hasRollable ? (
          <IconButton size='sm' onClick={onReroll} aria-label={t('task.settings.variables.reroll')}>
            <Dices aria-hidden='true' className='h-3.5 w-3.5' />
          </IconButton>
        ) : undefined}
      />

      {/* SORTED BY TYPE, no longer headed by one.
          The column headers went when the open row started carrying its own labels — "Min"
          beside the Min field says more than "Min" in a strip above three rows, and a constant
          and an equation never shared those headings anyway. The glyph names the type; the order
          groups it: constants first (the givens), then randoms, then the equations built on them.

          LOOSE variables only. A set's members live under their set, which is the whole point of
          having one. */}
      {loose.length > 0 && (
        <div className='mb-3 flex flex-col gap-0.5'>
          {loose.map(v => row(v, false))}
        </div>
      )}

      {/* ONE BLOCK PER SET, including a set that has nothing in it yet — that one exists only in
          panel state, because an empty set has no member to carry its name and nothing to store.
          It is draft furniture, exactly like a variable with no name. */}
      {sets.map(set => {
        const members = variables.filter(v => v.set === set);
        const blocked = setBlockers[set] ?? false;
        const open = expandedSets.includes(set);
        const setArmed = armedId === `set:${set}`;
        return (
          // ARMED HIGHLIGHTS THE WHOLE CARD, which is the convention every other delete in this
          // app follows and the only honest preview here: one more press takes the set and every
          // member in it, so tinting the button alone understates what is about to go.
          <div key={set} className={`mb-3 rounded-lg border transition-colors ${
            setArmed
              ? 'border-ws-warning-fg bg-ws-warning-bg/50'
              : 'border-ws-border'
          }`}>
            <SetHeader
              set={set}
              members={members}
              open={open}
              roomyTargets={roomyTargets}
              allNames={allNames}
              rolls={rolls}
              onToggle={() => setExpandedSets(prev =>
                prev.includes(set) ? prev.filter(x => x !== set) : [...prev, set])}
              onRename={next => renameSet(set, next, members)}
            />
            {open && (
              <div className='flex flex-col gap-0.5 px-2 pb-2'>
                {members.map(v => row(v, true))}
                <SetFooter
                  set={set}
                  blocked={blocked}
                  armed={setArmed}
                  roomyTargets={roomyTargets}
                  onAdd={onAdd && (kind => onAdd(kind, set))}
                  onArm={() => onArm(`set:${set}`)}
                  onDisarm={onDisarm}
                  onDuplicate={() => onDuplicateSet(set)}
                  onDelete={() => { onDisarm(); onDeleteSet(set); }}
                />
              </div>
            )}
          </div>
        );
      })}

      {/*
        * SYMBOLS ONLY, because this control now appears twice over — once per section and once
        * inside every set — and four labelled buttons do not fit a set header.
        *
        * It can afford to drop the words only because the glyphs are no longer new by the time
        * you meet them here: every row carries the same one. The labels stay as `aria-label` and
        * `title`, which is the rule for an icon-only button anyway, so nothing is lost to a
        * reader or to a hover.
        */}
      {onAdd && <AddKindButtons
        onAdd={onAdd}
        onAddSet={() => {
          // Open, unlike every other set: a brand-new one is EMPTY, and collapsed it shows its
          // name and the word "Empty" with no way to put anything in it.
          const name = freeSetName('set', [...sets, ...allNames]);
          setDraftSets(prev => [...prev, name]);
          setExpandedSets(prev => [...prev, name]);
        }}
        roomyTargets={roomyTargets}
        addBtnRef={addBtnRef} />}

    </section>
  );
}

export default function VariablesPanel({
  variables,
  experienceVariables,
  declinedNames = [],
  onRetryName,
  variableUsage = {},
  mayAdd = true,
  onAddVariable,
  onUpdateVariable,
  onRemoveVariable,
  onRenameSet,
  onDeleteSet,
  onDuplicateSet,
  onAddExperienceVariable,
  onUpdateExperienceVariable,
  onRemoveExperienceVariable,
  onRenameExperienceSet,
  onDeleteExperienceSet,
  onDuplicateExperienceSet,
  onClose,
  fullScreen = false,
}: VariablesPanelProps) {
  const { t } = useTranslation();
  const [armedId, setArmedId] = useState<string | null>(null);
  /*
   * ACCORDION, not collapse-on-blur.
   *
   * Opening another definition closes this one, clicking the same summary shuts it, Escape shuts
   * it. Nothing else does — in particular not focus leaving the row, which was the first idea and
   * is the one to stay away from: it needs `relatedTarget` tracking across a subtree so that
   * tabbing from Min to Max does not count as leaving, and it closes the row under a teacher who
   * reached for the scrollbar mid-edit. This app has been bitten by exactly that class of
   * focus-driven dismissal before.
   */
  const [expandedId, setExpandedId] = useState<string | null>(null);

  /** Both scopes, in the order a block sees them. */
  const everyVariable = [...experienceVariables, ...variables];
  // Only the DRAWS are state. The displayed values are derived from them on every render, so an
  // equation tracks its inputs as the teacher types rather than lagging a keystroke behind.
  const [draws, setDraws] = useState<Record<string, number>>(() => drawRandoms(everyVariable));
  const samples = sampleValues(everyVariable, draws);
  const rolls = samples.byId;

  /* Keyed on the RANGES, not just the ids. Keyed on ids alone, editing a range never re-ran this,
     so a draw outlived the definition that produced it. */
  const randomSignature = everyVariable.map(v => `${v.id}:${v.min}:${v.max}:${v.step}`).join('|');
  useEffect(() => {
    // Re-draw what is new or no longer possible, keep what still holds, drop what is gone.
    setDraws(prev => {
      const next = drawRandoms(everyVariable, prev);
      const same = Object.keys(next).length === Object.keys(prev).length
        && Object.keys(next).every(id => next[id] === prev[id]);
      return same ? prev : next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [randomSignature]);

  /*
   * THE TWO SCOPES DO NOT SHARE A DUPLICATE CHECK, and giving them one was a bug: the row only
   * commits a name when it has no error, so an experience variable typed as `river` while a task
   * defined `river` read as a duplicate, never committed, and never reached the promotion that
   * exists precisely to answer it. The teacher saw a dead end where the question should have been.
   *
   * Downward is a plain refusal: a TASK variable may not take an experience variable's name,
   * because the experience one is visible in the same panel and there is nothing to merge.
   *
   * Upward is a question, not an error: an EXPERIENCE variable taking a task's name is the
   * promotion. Only another EXPERIENCE variable's name is a duplicate here — plus any the
   * teacher has already declined, which is a name genuinely spoken for.
   */
  const experienceNames = [...experienceVariables.map(v => v.name), ...declinedNames];
  const taskNames = [...variables.map(v => v.name), ...experienceVariables.map(v => v.name)];
  /** Born with its type, from `fieldsForKind`, so the row draws the right fields immediately and
   *  `variableKind` reads it back as what was asked for. */
  const newVariable = (kind: VariableKind, set?: string): TaskVariable =>
    ({ id: `var_${crypto.randomUUID()}`, name: '', ...fieldsForKind(kind), ...(set ? { set } : {}) });

  return (
    /*
     * THE SHELL DOES NOT SCROLL; THE BODY DOES.
     *
     * It was `overflow-y-auto` here with the header inside it, so the header — and the X that
     * closes the panel — scrolled away on a long list, leaving no way out of the phone's
     * full-screen modal without scrolling back up for it.
     *
     * This is how the Flow frame is built: a clipped shell, a `shrink-0` header, and a
     * `flex-1 overflow-y-auto` body. `min-h-0` is what lets the body actually shrink and scroll
     * instead of pushing the shell taller than its parent.
     */
    <div className={`flex flex-col overflow-hidden bg-ws-surface ${fullScreen ? 'min-h-0 flex-1 w-full' : 'w-96 shrink-0 h-full rounded-xl border border-ws-primary/30'}`}>
      {/* Header */}
      <div className='flex shrink-0 items-center gap-2 border-b border-ws-primary/20 bg-ws-primary-subtle dark:bg-ws-text-link/[.20] px-4 py-2.5 text-base font-semibold text-ws-primary dark:text-ws-text-link'>
        <Braces aria-hidden='true' className='h-6 w-6' />
        <span className='flex-1'>{t('task.settings.title')}</span>
        <IconButton size='sm' onClick={onClose} aria-label={t('task.settings.close')}
          className='text-current hover:bg-black/10 dark:hover:bg-white/10'>
          <X aria-hidden='true' className='h-5 w-5' />
        </IconButton>
      </div>

      {/* Body — the one thing that scrolls, so the header above it stays put.

          NO TOP PADDING: a sticky child pins below its scroll container's padding, so `pt-4` here
          would leave a 16px strip above the pinned section heading with rows visibly sliding
          through it. `EditorSectionHeading`'s sticky branch carries that spacing instead. */}
      <div className='flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-4 pb-4'>
        {/* The experience's first, because it is the wider scope and the one a name collides
            with: seeing it above makes the rule below it legible without explaining it. */}
        <VariableScopeSection
          id='experience-variables'
          heading={t('task.settings.variables.experienceSectionTitle')}
          scope={t('task.settings.variables.experienceSectionScope')}
          variables={experienceVariables}
          allNames={experienceNames}
          onNameEdited={onRetryName}
          usage={variableUsage}
          everyVariable={everyVariable}
          rolls={rolls}
          rolledByName={samples.byName}
          armedId={armedId}
          onArm={setArmedId}
          onDisarm={() => setArmedId(null)}
          expandedId={expandedId}
          onExpand={setExpandedId}
          roomyTargets={fullScreen}
          divider={false}
          hasRollable={experienceVariables.some(isRollableVariable)}
          onReroll={() => setDraws(prev => ({ ...prev, ...drawRandoms(experienceVariables) }))}
          onAdd={mayAdd ? ((kind, set) => onAddExperienceVariable(newVariable(kind, set))) : undefined}
          onDeleteSet={onDeleteExperienceSet}
          onDuplicateSet={onDuplicateExperienceSet}
          onRenameSet={onRenameExperienceSet}
          onUpdate={onUpdateExperienceVariable}
          onRemove={onRemoveExperienceVariable}
        />

        <VariableScopeSection
          id='task-variables'
          heading={t('task.settings.variables.sectionTitle')}
          scope={t('task.settings.variables.sectionScope')}
          variables={variables}
          allNames={taskNames}
          usage={variableUsage}
          everyVariable={everyVariable}
          rolls={rolls}
          rolledByName={samples.byName}
          armedId={armedId}
          onArm={setArmedId}
          onDisarm={() => setArmedId(null)}
          expandedId={expandedId}
          onExpand={setExpandedId}
          roomyTargets={fullScreen}
          divider
          hasRollable={variables.some(isRollableVariable)}
          onReroll={() => setDraws(prev => ({ ...prev, ...drawRandoms(variables) }))}
          onAdd={mayAdd ? ((kind, set) => onAddVariable(newVariable(kind, set))) : undefined}
          onDeleteSet={onDeleteSet}
          onDuplicateSet={onDuplicateSet}
          onRenameSet={onRenameSet}
          onUpdate={onUpdateVariable}
          onRemove={onRemoveVariable}
        />

      </div>
    </div>
  );
}
